import { Extension } from '@tiptap/core'

export type MathCalculatorOptions = {
  /**
   * When set (≥ 0), the computed result is formatted with `toFixed(n)`.
   * When `null`, the raw numeric string is used (MarkdownEditor default).
   */
  resultDecimalPlaces: number | null
}

export function formatMathResult(value: number, decimalPlaces: number | null): string {
  if (decimalPlaces == null || decimalPlaces < 0) {
    return String(value)
  }
  return value.toFixed(Math.min(20, Math.floor(decimalPlaces)))
}

// Simple math expression evaluator
export function evaluateMathExpression(expression: string): number | null {
  try {
    // Remove any whitespace
    const cleanExpression = expression.replace(/\s/g, '')

    // Basic validation - only allow numbers, operators, parentheses, and decimal commas
    if (!/^[0-9+\-*/(),\s^]+$/.test(cleanExpression)) {
      return null
    }

    // Convert comma decimal separators to dots for JavaScript evaluation
    // Convert ^ to ** for JavaScript exponentiation
    const jsExpression = cleanExpression.replace(/,/g, '.').replace(/\^/g, '**')

    // Use Function constructor for safe evaluation (more secure than eval)
    // This only allows basic math operations
    const result = new Function('return ' + jsExpression)()

    // Check if result is a valid number
    if (typeof result === 'number' && !isNaN(result) && isFinite(result)) {
      // Round to avoid floating point precision issues
      return Math.round(result * 1000000) / 1000000
    }

    return null
  } catch (error) {
    return null
  }
}

function tryReplaceFormulaAtLineEnd(line: string, decimalPlaces: number): string {
  const match = /^(.*?)(\s*)([0-9+\-*/(),\s^]+)\s*=\s*$/.exec(line)
  if (!match) return line

  const prefix = match[1] ?? ''
  const gap = match[2] ?? ''
  const expression = match[3].trim()

  if (
    !/^[0-9+\-*/(),\s^]+$/.test(expression) ||
    expression.length < 3 ||
    !/[+\-*/^]/.test(expression)
  ) {
    return line
  }

  const result = evaluateMathExpression(expression)
  if (result === null) return line

  return `${prefix}${gap}${expression} = ${formatMathResult(result, decimalPlaces)} `
}

/**
 * Any line ending with `…expr = ` (optional text before the expression) becomes `…expr = result `.
 * When `caretIndex` is the end of a line that was replaced, returned `caretIndex` is that line’s end after replace.
 */
export function replaceTrailingFormulaPlainText(
  text: string,
  decimalPlaces: number,
  caretIndex: number | null = null,
): { text: string; caretIndex: number | null } {
  const lines = text.split(/\r?\n/)
  let index = 0
  let nextCaret: number | null = caretIndex

  const nextLines = lines.map((line, i) => {
    const lineStart = index
    const oldLen = line.length
    const newLine = tryReplaceFormulaAtLineEnd(line, decimalPlaces)
    index += newLine.length
    if (i < lines.length - 1) index += 1

    if (newLine !== line && caretIndex !== null && caretIndex === lineStart + oldLen) {
      nextCaret = lineStart + newLine.length
    }

    return newLine
  })

  return { text: nextLines.join('\n'), caretIndex: nextCaret }
}

export const MathCalculator = Extension.create<MathCalculatorOptions>({
  name: 'mathCalculator',

  addOptions() {
    return {
      resultDecimalPlaces: null,
    }
  },

  addInputRules() {
    const decimalPlaces = this.options.resultDecimalPlaces

    return [
      {
        // Match patterns like "3 + 4 = " or "10 * 2,5 = " or "(3 + 4) * 2 = " or "2^3 = "
        // This regex captures leading spaces separately from the math expression
        find: /(\s*)([0-9+\-*/(),\s^]+)\s*=\s*$/,
        handler: ({ range, match, commands }) => {
          // Extract the leading spaces and math expression
          const leadingSpaces = match[1] || ''
          const expression = match[2].trim()

          // Only process if the expression looks like a valid math expression
          // Must have at least one operator and be at least 3 characters
          if (
            !/^[0-9+\-*/(),\s^]+$/.test(expression) ||
            expression.length < 3 ||
            !/[+\-*/^]/.test(expression)
          ) {
            return
          }

          // Evaluate the expression
          const result = evaluateMathExpression(expression)

          if (result !== null) {
            // Replace the entire match with leading spaces + expression + "= " + result
            const replacement = `${leadingSpaces}<mark style="background-color: #dbdbdb;">${expression} = ${formatMathResult(
              result,
              decimalPlaces,
            )}</mark> `

            // Use commands to replace the text
            commands.deleteRange(range)
            commands.insertContent(replacement)
          }
        },
        undoable: true,
      },
    ]
  },
})

export default MathCalculator
